import { useCallback, useEffect, useRef, useState } from "react";
import { Input, Tooltip } from "@renderbus/ui";
import { useClickOutside } from "project-config/hooks/useClickOutside";
import { useInputFocus } from "project-config/hooks";
import { ISubAccountData } from "@/store/SubAccountStore";
import EditIcon from "@/assets/icons/childmanager/edit";
import { RemarkCellDisplay, RemarkCellWrapper } from "./atom";
import { formatRemarkText } from "./helper";
import { useSaveRemark } from "./hooks/use-save-remark";

type IRemarkCellProps = Pick<ISubAccountData, "id" | "remark" | "deptId"> & {
  isDingTalk?: boolean;
};

/**
 * 备注单元格：悬浮展示全文 tooltip + 编辑 icon，点击进入行内编辑，
 * 失焦/回车自动提交。超 20 字符截断加省略号。
 */
export function RemarkCell({
  id,
  remark,
  deptId,
  isDingTalk,
}: IRemarkCellProps) {
  const saveRemark = useSaveRemark({ isDingTalk, deptId });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(remark);
  const ref = useRef<HTMLDivElement>(null);
  // 请求进行中标记：useClickOutside 对单个 ref 会重复触发回调，
  // 两次 commit 读到的 editing 闭包值同为 true，需挡住重复请求
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(remark);
  }, [remark, editing]);

  // 提交：先退出编辑态以解除外部监听，避免重复触发；值未变更则不发请求
  const commit = useCallback(() => {
    if (!editing || submittingRef.current) return;
    setEditing(false);
    if (draft === remark) return;
    submittingRef.current = true;
    saveRemark(id, draft).finally(() => {
      submittingRef.current = false;
    });
  }, [editing, draft, remark, id, saveRemark]);

  useClickOutside(ref, commit, editing, [editing, draft]);
  useInputFocus(".remark-cell-edit", editing, [editing]);

  return (
    <RemarkCellWrapper ref={ref}>
      <RemarkCellDisplay onClick={() => setEditing(true)} $hidden={editing}>
        <Tooltip
          placement="top"
          title={remark}
          color="#202124"
          triggerBody={document.body}
          tooltipText={
            <span className="remark-cell-text">{formatRemarkText(remark)}</span>
          }
        />
        <EditIcon className="remark-cell-edit-icon" />
      </RemarkCellDisplay>
      {editing && (
        <Input
          className="remark-cell-edit"
          value={draft}
          maxLength={50}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={commit}
        />
      )}
    </RemarkCellWrapper>
  );
}
